import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import {
  UploadCloud,
  CheckCircle2,
  AlertCircle,
  FileText,
  Calendar,
  Lock,
  ArrowRight,
  ShieldAlert,
} from 'lucide-react';
import { compressAndHashImage, CompressionResult } from '../utils/imageCompressor';
import { submitContributionApi } from '../services/apiClient';
import {
  toDhakaDateTime,
  calculateLateFee,
  getMonthsBetween,
} from '../../../../shared/utilities/calculations';

export const SubmitPayment: React.FC = () => {
  const { t } = useTranslation();
  const { user, member, emailVerified, isActive } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const nowDhaka = toDhakaDateTime();
  const queryParams = new URLSearchParams(location.search);
  const preselectedMonth = queryParams.get('month') || nowDhaka.isoMonth;

  const [month, setMonth] = useState(preselectedMonth);
  const [compressing, setCompressing] = useState(false);
  const [compressionResult, setCompressionResult] = useState<CompressionResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Month list: previous 3 months up to next month
  const availableMonths = getMonthsBetween('2024-01', nowDhaka.isoMonth);

  // Calculate late fee dynamically based on selected month and current Dhaka time
  const lateFee = calculateLateFee(month, new Date());
  const baseAmount = 5000;
  const totalAmount = baseAmount + lateFee;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setCompressing(true);

    try {
      const result = await compressAndHashImage(file);
      setCompressionResult(result);
    } catch (err: any) {
      setError(err.message || 'Failed to process image.');
      setCompressionResult(null);
    } finally {
      setCompressing(false);
    }
  };

  const generateUuid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!compressionResult) {
      setError('Please select and upload a payment receipt screenshot.');
      return;
    }

    if (!emailVerified) {
      setError('Your email must be verified before submitting payment proofs.');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const idempotencyKey = generateUuid();
      await submitContributionApi({
        month,
        amount: baseAmount,
        proofFile: compressionResult.file,
        idempotencyKey,
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Payment submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (n: number) => `৳${n.toLocaleString('en-US')}`;
  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-4 sm:py-8 pb-24 sm:pb-8">
      <div className="bg-white dark:bg-navy-900 rounded-2xl sm:rounded-3xl border border-gray-200 dark:border-navy-800 shadow-xl p-6 sm:p-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t('submit.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('submit.subtitle')}
          </p>
        </div>

        {/* Error notification */}
        {error && (
          <div className="mb-5 p-3.5 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 flex items-start gap-2.5 text-red-700 dark:text-red-300 text-xs sm:text-sm">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Success notification */}
        {success && (
          <div className="mb-5 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 flex items-start gap-2.5 text-emerald-700 dark:text-emerald-300 text-xs sm:text-sm font-medium">
            <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <span>{t('submit.successMessage')} Redirecting...</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Month selector */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1.5">
              {t('submit.selectMonth')}
            </label>
            <div className="relative">
              <Calendar className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-gray-50 dark:bg-navy-950/60 border border-gray-300 dark:border-navy-700 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-gray-900 dark:text-white font-medium"
              >
                {availableMonths.map((m) => (
                  <option key={m} value={m}>
                    {m} {m === nowDhaka.isoMonth ? '(Current)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Breakdown Card */}
          <div className="p-4 rounded-xl bg-gray-50 dark:bg-navy-950/40 border border-gray-200/80 dark:border-navy-800 space-y-2 text-xs sm:text-sm">
            <div className="flex justify-between text-gray-600 dark:text-gray-400">
              <span>{t('submit.baseAmount')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{fmt(baseAmount)}</span>
            </div>
            <div className="flex justify-between text-gray-600 dark:text-gray-400">
              <span>{t('submit.lateFee')}</span>
              <span className={`font-semibold ${lateFee > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500'}`}>
                {fmt(lateFee)}
              </span>
            </div>
            <div className="border-t border-gray-200 dark:border-navy-800 pt-2 flex justify-between font-bold text-gray-900 dark:text-white text-sm sm:text-base">
              <span>{t('submit.totalCharged')}</span>
              <span className="text-primary-600 dark:text-primary-400">{fmt(totalAmount)}</span>
            </div>
          </div>

          {/* Screenshot Upload & Compression Box */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1.5">
              {t('submit.uploadProof')}
            </label>

            <div className="border-2 border-dashed border-gray-300 dark:border-navy-700 rounded-2xl p-4 sm:p-6 text-center hover:border-primary-500 dark:hover:border-primary-400 transition-colors relative">
              <input
                type="file"
                accept="image/jpeg,image/png"
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={compressing || submitting}
              />

              {compressing ? (
                <div className="py-4 text-xs sm:text-sm text-gray-500">
                  Compressing and hashing image on device...
                </div>
              ) : compressionResult ? (
                <div className="space-y-3">
                  <img
                    src={compressionResult.previewUrl}
                    alt="Deposit Receipt Preview"
                    className="max-h-48 mx-auto rounded-lg shadow-sm border border-gray-200 dark:border-navy-700"
                  />
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    <p className="font-medium text-emerald-600 dark:text-emerald-400">
                      ✓ {t('submit.clientCompressed')} {formatBytes(compressionResult.compressedSize)} (from {formatBytes(compressionResult.originalSize)})
                    </p>
                    <p className="font-mono text-[10px] text-gray-400 mt-1 truncate">
                      SHA-256: {compressionResult.sha256}
                    </p>
                    <p className="text-[11px] text-primary-600 hover:underline mt-1 cursor-pointer">
                      Click to choose another photo
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 py-4">
                  <UploadCloud className="w-8 h-8 mx-auto text-primary-500" />
                  <p className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {t('submit.dragDrop')}
                  </p>
                  <p className="text-[11px] text-gray-400">{t('submit.formatHint')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!compressionResult || compressing || submitting || success}
            className="w-full py-3.5 px-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold text-sm shadow-md shadow-primary-600/30 flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <span>{t('submit.submitting')}</span>
            ) : (
              <>
                <span>{t('submit.submitBtn')}</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};
